import React, { useEffect, useState, Children } from 'react';
import { LeafIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface SplashScreenProps {
  visible: boolean;
  userName?: string;
}
export function SplashScreen({
  visible,
  userName
}: SplashScreenProps) {
  const [statusIndex, setStatusIndex] = useState(0);
  const statuses = ['Opening drainage pathways...', 'Preparing your protocol...', userName ? `Welcome back, ${userName}` : 'Welcome to The Drainage Code'];
  useEffect(() => {
    if (!visible) return;
    const timer1 = setTimeout(() => setStatusIndex(1), 2800);
    const timer2 = setTimeout(() => setStatusIndex(2), 3800);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [visible]);
  // Generate random particles
  const particles = Array.from({
    length: 8
  }).map((_, i) => ({
    id: i,
    size: Math.random() * 8 + 4,
    left: `${Math.random() * 100}%`,
    delay: Math.random() * 5,
    duration: Math.random() * 10 + 10
  }));
  return <AnimatePresence data-id="element-369">
      {visible && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0,
      scale: 1.03
    }} transition={{
      duration: 0.8,
      ease: 'easeInOut'
    }} className="fixed inset-0 bg-gradient-to-br from-[#1a3320] via-[#2C4A30] to-[#4A7A50] flex flex-col items-center justify-center z-[1000] overflow-hidden" data-id="element-370">
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-371">
            {particles.map(p => <div key={p.id} className="absolute rounded-full bg-white/20 animate-float-up" style={{
          width: p.size,
          height: p.size,
          left: p.left,
          animationDelay: `${p.delay}s`,
          animationDuration: `${p.duration}s`
        }} data-id="element-372" />)}
          </div>

          <motion.div initial="hidden" animate="visible" variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.3,
            delayChildren: 0.5
          }
        }
      }} className="relative z-10 flex flex-col items-center" data-id="element-373">
            {/* Icon Container */}
            <motion.div variants={{
          hidden: {
            scale: 0.8,
            opacity: 0
          },
          visible: {
            scale: 1,
            opacity: 1,
            transition: {
              type: 'spring',
              stiffness: 100,
              damping: 20
            }
          }
        }} className="relative mb-8" data-id="element-374">
              {/* Radiating Rings */}
              <div className="absolute inset-0 rounded-[28px] border border-white/30 animate-radiate" data-id="element-375" />
              <div className="absolute inset-0 rounded-[28px] border border-white/30 animate-radiate" style={{
            animationDelay: '0.5s'
          }} data-id="element-376" />

              <div className="w-[90px] h-[90px] bg-white/10 rounded-[28px] flex items-center justify-center backdrop-blur-xl border border-white/20 relative z-10" data-id="element-377">
                <motion.div animate={{
              rotate: [0, 10, 0]
            }} transition={{
              duration: 4,
              ease: 'easeInOut',
              repeat: Infinity
            }} data-id="element-378">
                  <LeafIcon className="w-11 h-11 text-white" data-id="element-379" />
                </motion.div>
              </div>
            </motion.div>

            {/* Text Content */}
            <motion.div variants={{
          hidden: {
            y: 20,
            opacity: 0
          },
          visible: {
            y: 0,
            opacity: 1,
            transition: {
              duration: 0.8,
              ease: 'easeOut'
            }
          }
        }} className="text-white text-3xl font-extrabold tracking-tight mb-2" data-id="element-380">
              The Drainage Code
            </motion.div>

            <motion.div variants={{
          hidden: {
            opacity: 0,
            letterSpacing: '6px'
          },
          visible: {
            opacity: 1,
            letterSpacing: '3px',
            transition: {
              duration: 1,
              ease: 'easeOut'
            }
          }
        }} className="text-white/75 text-sm uppercase mb-12" data-id="element-381">
              7-Day Lymphatic Reset
            </motion.div>

            {/* Loading Bar & Status */}
            <motion.div variants={{
          hidden: {
            opacity: 0
          },
          visible: {
            opacity: 1,
            transition: {
              delay: 1.5,
              duration: 0.5
            }
          }
        }} className="flex flex-col items-center w-full max-w-[240px]" data-id="element-382">
              <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mb-4" data-id="element-383">
                <motion.div initial={{
              width: '0%'
            }} animate={{
              width: '100%'
            }} transition={{
              duration: 2.5,
              delay: 1.8,
              ease: 'easeInOut'
            }} className="h-full bg-gradient-to-r from-gold-light to-gold rounded-full" data-id="element-384" />
              </div>

              <div className="h-5 relative w-full flex justify-center" data-id="element-385">
                <AnimatePresence mode="wait" data-id="element-386">
                  <motion.div key={statusIndex} initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} transition={{
                duration: 0.3
              }} className="absolute text-white/60 text-xs font-medium tracking-wide" data-id="element-387">
                    {statuses[statusIndex]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}